import { Payments, TokenResult } from '@square/web-payments-sdk-types';
import SqFetch from 'typescript/application/sq_fetch';
import { createVerificationDetails } from 'typescript/utilities/web_payment_sdk';
import { CardIntent } from 'typescript/constants/enums/card';
import { TOrderSummary, OrderAddress } from 'typescript/types/api/solidshop';

interface PaymentDataPayload {
  card_data?: {
    card_brand: string;
    last_4: string;
    exp_year: number;
    exp_month: number;
    postalCode?: string;
  };
  cashtag?: string;
}

const createPaymentDataPayload = (
  tokenResult: TokenResult,
): PaymentDataPayload => {
  if (tokenResult.details?.cashAppPay?.cashtag) {
    return { cashtag: tokenResult.details.cashAppPay.cashtag };
  } else {
    const cardData = tokenResult.details.card!;
    const postalCode = tokenResult.details.billing?.postalCode;
    return {
      card_data: {
        card_brand: cardData.brand,
        last_4: cardData.last4,
        exp_year: cardData.expYear,
        exp_month: cardData.expMonth,
        postalCode,
      },
    };
  }
};

const getCardIntent = (
  orderData: TOrderSummary,
  saveCardToWallet: boolean,
): CardIntent => {
  if (saveCardToWallet) {
    return CardIntent.CHARGE_AND_STORE;
  }
  const intent = orderData.card_intent?.intent?.toUpperCase();
  if (intent && Object.values(CardIntent).includes(intent as CardIntent)) {
    return intent as CardIntent;
  }

  return CardIntent.CHARGE;
};

interface CreateShopPaymentWithSavedCardRequestParams {
  order_number: string;
  wallet_payment_source_id: string;
  forter_token: string;
  save_to_wallet: boolean;
  card_data: {
    card_brand: string;
    last_4: string;
    exp_year: number;
    exp_month: number;
    postalCode: string;
  };
}

const createShopPaymentWithSavedCard = (
  orderNumber: string,
  selectedCard: any,
): Promise<Response> => {
  const requestParams: CreateShopPaymentWithSavedCardRequestParams = {
    order_number: orderNumber,
    wallet_payment_source_id: selectedCard.id,
    forter_token: window.ftrToken,
    save_to_wallet: false,
    card_data: {
      card_brand: selectedCard.payment_source.brand,
      last_4: selectedCard.payment_source.last_digits,
      exp_year: selectedCard.payment_source.year,
      exp_month: selectedCard.payment_source.month,
      postalCode: selectedCard.payment_source?.address?.postal_code || '',
    },
  };

  return SqFetch.post('../../api/v1/payments', requestParams);
};

interface CreateShopPaymentWithTokenRequestParams extends PaymentDataPayload {
  order_number: string;
  nonce: string;
  verification_token: string;
  forter_token: string;
  issuer_number_of_installments: number | null;
  save_to_wallet: boolean;
  wallet_payment_source_id: string | null;
}

const createShopPaymentWithToken = async (
  orderNumber: string,
  orderData: TOrderSummary,
  tokenResult: TokenResult,
  sqPayments: Payments,
  issuerInstallmentsTerm: number | null = null,
  saveCardToWallet = false,
  billingAddress?: OrderAddress,
): Promise<Response> => {
  const cardIntent = getCardIntent(orderData, saveCardToWallet);
  const scaResponse = await sqPayments.verifyBuyer(
    tokenResult.token,
    createVerificationDetails(orderData, cardIntent, billingAddress),
  );

  const requestParams: CreateShopPaymentWithTokenRequestParams = {
    order_number: orderNumber,
    nonce: tokenResult.token,
    verification_token: scaResponse.token, // buyer verification token
    forter_token: window.ftrToken,
    issuer_number_of_installments: issuerInstallmentsTerm,
    save_to_wallet: saveCardToWallet,
    wallet_payment_source_id: null,
    ...createPaymentDataPayload(tokenResult),
  };

  return SqFetch.post('../../api/v1/payments', requestParams);
};

interface PreAuthRequestParams extends PaymentDataPayload {
  order_number: string;
  nonce: string;
  forter_token: string;
}

const checkPreAuthFraud = (
  orderNumber: string,
  tokenResult: TokenResult,
): Promise<Response | null> => {
  try {
    const requestParams: PreAuthRequestParams = {
      order_number: orderNumber,
      nonce: tokenResult.token,
      forter_token: window.ftrToken,
      ...createPaymentDataPayload(tokenResult),
    };
    return SqFetch.post('../../api/v1/pre-auth', requestParams);
  } catch {
    return null;
  }
};

export {
  createShopPaymentWithToken,
  createShopPaymentWithSavedCard,
  checkPreAuthFraud,
};
